import { Download, Eye, FileIcon, Pencil, RefreshCw, RotateCw, Trash2 } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import DeleteModal from '../CommonComponents/DeleteModal';
import ToastMessage from './ToastMessage';

type ColumnKey<T> = keyof T | 'actions' | 'viewProject';

export interface Column<T> {
  key: ColumnKey<T>;
  header: string;
  render?: (value: any, row: T) => ReactNode;
  onChange?: (value: any, row: T) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  striped?: boolean;
  enableFilters?: boolean;
  statusOptions?: string[];
  projectOptions?: string[];
  actions?: (row: T) => ReactNode;
  listHeader?: React.ReactNode;
  listTitle: string;
  // Callbacks for actions
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onPreview?: (row: T) => void;
  onDelete?: (row: T) => void;
  onStatusToggle?: (id: string, newStatus: boolean) => void;
  onExport?: (row: T) => void;
  onRefresh?: (row: T) => void;
  permissions?: Record<string, boolean>;
  loading?: boolean;
}

const DataTable = <T extends { [key: string]: any }>({
  columns,
  data,
  striped = true,
  enableFilters = false,
  statusOptions = ['All Status', 'Active', 'Inactive'],
  projectOptions = ['All Projects', 'Project 1', 'Project 2', 'Project 3'],
  listTitle,
  listHeader,
  onView,
  onEdit,
  onPreview,
  onDelete,
  onStatusToggle,
  onExport,
  onRefresh,
  permissions,
  loading = false,
}: DataTableProps<T>) => {
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [projectFilter, setProjectFilter] = useState('All Projects');
  const [tableData, setTableData] = useState(data);
  const [deleteRow, setDeleteRow] = useState<T | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adminIdToDelete, setAdminIdToDelete] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
    // Return undefined when toast is null
    return undefined;
  }, [toast]);

  // ✅ Keep local data synced when props.data changes
  useEffect(() => {
    // safeguard for mapped data from parent (systemAdmin[])
    if (Array.isArray(data)) {
      setTableData(data);
    }
  }, [data]);

  const handleStatusToggle = (row: any) => {
    if (row.status === 'DRAFT') return;

    const current =
      row.status === true || row.status === 'Active' || row.status === 'ACTIVE' || row.isActive;

    const newStatus = !current;

    setTableData((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, status: newStatus ? 'Active' : 'Inactive' } : item,
      ),
    );

    if (onStatusToggle) {
      onStatusToggle(String(row.id), newStatus);
    }
  };

  const filteredData = tableData.filter((row) => {
    const matchesStatus = statusFilter === 'All Status' || row.status === statusFilter;
    const matchesProject = projectFilter === 'All Projects' || row.project === projectFilter;
    return matchesStatus && matchesProject;
  });

  return (
    <div className="flex flex-col border border-[#E0C9D8] overflow-hidden h-full">
      {toast && (
        <ToastMessage type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}

      {listTitle && listHeader && (
        <div className="bg-[#F2EBEE] text-[#621C43] px-4 py-3 border-b border-[#E0C9D8] flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">{listTitle}</h2>
          {listHeader && <div>{listHeader}</div>}
        </div>
      )}

      {/* Filters */}
      {enableFilters && (
        <div className="bg-[#F2EBEE] flex gap-4 px-4 py-3 border-b border-[#E0C9D8] flex-shrink-0">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#D1A3B6] bg-[#F2EBEE] rounded px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-[#AD0C62]"
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="border border-[#D1A3B6] bg-[#F2EBEE] rounded px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-[#AD0C62]"
          >
            {projectOptions.map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <div className="max-h-[500px] md:max-h-[500px] lg:max-h-[500px] overflow-y-auto custom-scrollbar">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-[#AD0C62] text-[#FFFFFF] text-sm tracking-wide sticky top-0 z-20">
              <tr>
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className="px-4 py-3 font-semibold whitespace-nowrap bg-[#AD0C62] text-center"
                  >
                    {col.header}
                  </th>
                ))}

                <th className="px-4 py-3 font-semibold whitespace-nowrap bg-[#AD0C62] text-center">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-10 text-center text-[#621C43]"
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                      <RefreshCw className="size-8 animate-spin" />
                      <p className="text-sm">Hold on! Fetching the data</p>
                    </div>
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((row, i) => (
                  <tr
                    key={i}
                    className={`${striped && i % 2 === 0 ? 'bg-[#F2EBEE]' : 'bg-[#EBD5E0]'}`}
                  >
                    {columns.map((col) => (
                      <td
                        key={String(col.key)}
                        className="px-4 py-3 text-[#4A082D] whitespace-nowrap font-medium capitalize text-center"
                      >
                        {col.render ? (
                          col.render(row[col.key], row)
                        ) : col.key === 'status' || col.key === 'isActive' ? (
                          row.status === 'DRAFT' ? (
                            // <span className="inline-block bg-[#F4A12C] text-white px-3 py-1 rounded-full text-xs font-medium">
                            //   Draft
                            // </span>

                            <div className="relative w-[85px] h-[24px] flex items-center justify-center rounded-full bg-[#F4A12C]">
                              <span className="text-xs font-semibold text-white">Draft</span>
                            </div>
                          ) : (
                            <div
                              onClick={() => {
                                if (!row._permissions?.edit) {
                                  return;
                                }
                                handleStatusToggle(row);
                              }}
                              className={`relative w-[85px] h-[24px] flex items-center rounded-full  transition-all duration-300
                                ${
                                  !row._permissions?.edit
                                    ? 'opacity-40 cursor-not-allowed'
                                    : 'cursor-pointer'
                                }
                            ${
                              row.status === true ||
                              row.status === 'Active' ||
                              row.status === 'ACTIVE' ||
                              row.isActive
                                ? 'bg-[#0BA96FCC]'
                                : 'bg-[#9E7C8DCC]'
                            }`}
                            >
                              {/* White sliding knob */}
                              <div
                                className={`absolute w-[26px] h-[16px] rounded-full shadow-md transform transition-all duration-300 bg-white
                              ${
                                row.status === true ||
                                row.status === 'Active' ||
                                row.status === 'ACTIVE' ||
                                row.isActive
                                  ? 'translate-x-[55px]'
                                  : 'translate-x-[4px]'
                              }`}
                              ></div>

                              {/* Text that moves opposite to knob */}
                              <span
                                className={`absolute text-xs font-semibold text-[#1A0206] transition-all duration-300
                              ${
                                row.status === true ||
                                row.status === 'Active' ||
                                row.status === 'ACTIVE' ||
                                row.isActive
                                  ? 'left-[8px]'
                                  : 'right-[8px]'
                              }`}
                              >
                                {row.status === true ||
                                row.status === 'Active' ||
                                row.status === 'ACTIVE' ||
                                row.isActive
                                  ? 'Active'
                                  : row.status === 'DRAFT'
                                  ? 'Draft'
                                  : 'Inactive'}
                              </span>
                            </div>
                          )
                        ) : col.key === 'tableData' ? (
                          /* Custom Status For Notifications*/
                          <span
                            className={`
                        inline-block px-3 py-1 text-center rounded-full text-xs font-Regular min-w-[100px]
                        ${
                          row.tableData === 'DRAFT'
                            ? 'bg-[#E24A4A]/80 text-[#1A0206]'
                            : row.tableData === 'SCHEDULED'
                            ? 'bg-[#4C86E7]/80 text-[#1A0206]'
                            : row.tableData === 'FAILED'
                            ? 'bg-[#F4A12C]/80 text-[#1A0206]'
                            : row.tableData === 'SENT'
                            ? 'bg-[#0BA96F]/80 text-[#1A0206]'
                            : 'bg-gray-300 text-green-700'
                        }
                      `}
                          >
                            {row.tableData?.toLowerCase()}
                          </span>
                        ) : col.key === 'type' ? (
                          <span
                            className={`
                        inline-block min-w-[100px] lowercase first-letter:uppercase text-center px-3 py-1 rounded-full text-xs font-Regular
                        ${
                          row.type === 'ADJUSTMENT'
                            ? 'bg-[#4C86E7CC]/80 text-[#1A0206]'
                            : row.type === 'CREDIT'
                            ? 'bg-[#0BA96FCC]/80 text-[#1A0206]'
                            : 'bg-[#E24A4ACC] text-[#1A0206]'
                        }
                      `}
                          >
                            {row.type}
                          </span>
                        ) : col.key === 'paymentStatus' ? (
                          <span
                            className={`
                        inline-block min-w-[100px] lowercase first-letter:uppercase text-center px-3 py-1 rounded-full text-xs font-Regular
                        ${
                          row.paymentStatus === 'PENDING'
                            ? 'bg-[#4C86E7CC]/80 text-[#1A0206]'
                            : row.paymentStatus === 'COMPLETED'
                            ? 'bg-[#0BA96FCC]/80 text-[#1A0206]'
                            : 'bg-[#E24A4ACC] text-[#1A0206]'
                        }
                      `}
                          >
                            {row.paymentStatus}
                          </span>
                        ) : (
                          String(row[col.key])
                        )}
                      </td>
                    ))}

                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-2 justify-center">
                        {onView && (
                          <Eye
                            className="w-4 h-4 cursor-pointer hover:text-[#AD0C62]"
                            onClick={() => onView(row)}
                            strokeWidth={1.5}
                          />
                        )}
                        {onEdit && (
                          <Pencil
                            className={`w-4 h-4 ${
                              row._permissions?.edit
                                ? 'cursor-pointer hover:text-[#AD0C62]'
                                : 'opacity-40 cursor-not-allowed'
                            }`}
                            onClick={() => {
                              if (row._permissions?.edit) onEdit(row);
                            }}
                            strokeWidth={1.5}
                          />
                        )}
                        {onPreview && (
                          <FileIcon
                            className="w-4 h-4 cursor-pointer hover:text-[#AD0C62]"
                            onClick={() => onPreview(row)}
                            strokeWidth={1.5}
                          />
                        )}
                        {onDelete && (
                          <Trash2
                            className={`w-4 h-4 ${
                              row._permissions?.delete
                                ? 'cursor-pointer hover:text-[#AD0C62]'
                                : 'opacity-40 cursor-not-allowed'
                            }`}
                            onClick={() => {
                              if (row._permissions?.delete) onDelete(row);
                            }}
                            strokeWidth={1.5}
                          />
                        )}
                        {onExport && (
                          <Download
                            className={`w-4 h-4 ${
                              row._permissions?.export
                                ? 'cursor-pointer hover:text-[#AD0C62]'
                                : 'opacity-40 cursor-not-allowed'
                            }`}
                            onClick={() => {
                              if (row._permissions?.export) onExport(row);
                            }}
                          />
                        )}

                        {onRefresh && row.status?.toUpperCase() === 'FAILED' && (
                          <RotateCw
                            className="w-4 h-4 cursor-pointer  hover:text-[#7a0847]"
                            onClick={() => onRefresh(row)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-6 text-center text-[#4A082D] whitespace-nowrap"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ Delete Modal */}
      {isDeleteModalOpen && (
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={() => {
            if (adminIdToDelete && onDelete) {
              onDelete(deleteRow!);
              setIsDeleteModalOpen(false);
              setToast({
                type: 'success',
                message: 'Record deleted successfully.',
              });
              setAdminIdToDelete(null);
            }
          }}
          // pageTitle={listTitle}
          itemName={
            deleteRow ? String(deleteRow.name || deleteRow.title || 'this item') : undefined
          }
        />
      )}
    </div>
  );
};

export default DataTable;
